import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Reservation } from '../models/Reservation'
import { reservationSchema } from '../models/schema'
import {
  createReservation as createReservationAPI,
  deleteReservation as deleteReservationAPI,
  fetchReservationById,
  fetchReservations,
  updateReservation as updateReservationAPI,
} from '../services/ReservationService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Reservation> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'reservation',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getReservationsSuccess,
  setQuery: setReservationQuery,
  reset: resetReservationState,
  resetQueryAndIds: resetReservationQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Reservation }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectReservations = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedReservation = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectReservationById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectReservationsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedReservation =
  (reservation: Reservation | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<Reservation>(reservation))
    )

export const getReservations = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchReservations(params),
    [reservationSchema],
    slice.actions
  )

export const createReservation = (reservation: Reservation) =>
  SBAPI.CreateDispatched(
    createReservationAPI(reservation),
    reservationSchema,
    slice.actions
  )

export const getReservationWithId = (id: number) =>
  SBAPI.FetchDispatched<Reservation>(
    fetchReservationById(id),
    reservationSchema,
    slice.actions
  )

export const updateReservation = (reservation: Reservation) =>
  SBAPI.UpdateDispatched<Reservation>(
    updateReservationAPI(reservation),
    reservation,
    slice.actions
  )

export const deleteReservation = (reservation: Reservation) =>
  SBAPI.DestroyDispatched<Reservation>(
    deleteReservationAPI(reservation),
    reservation,
    slice.actions
  )
